import jsPDF from 'jspdf';
import type { BeadDesign, BeadPalette, ExportOptions } from '../types';
import { getPaletteColor } from '../data/palette';
import { colorName, csvHeaders, t, type Locale } from '../i18n';
import { totalBeads } from './pattern';

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function renderDesignToCanvas(design: BeadDesign, palette: BeadPalette, options: ExportOptions) {
  const canvas = document.createElement('canvas');
  canvas.width = design.width * options.scale;
  canvas.height = design.height * options.scale;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Canvas export context is unavailable.');

  if (options.background === 'white') {
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
  }

  design.cells.forEach((colorId, index) => {
    const x = index % design.width;
    const y = Math.floor(index / design.width);
    if (colorId === 'transparent') {
      if (options.background === 'transparent') return;
      const checker = (x + y) % 2 === 0 ? '#f2f5f4' : '#e6ecea';
      context.fillStyle = checker;
    } else {
      context.fillStyle = getPaletteColor(palette, colorId).hex;
    }
    context.fillRect(x * options.scale, y * options.scale, options.scale, options.scale);

    if (options.includeSymbols && colorId !== 'transparent' && options.scale >= 14) {
      const color = getPaletteColor(palette, colorId);
      context.fillStyle = color.hex === '#FFFFFF' || color.hex === '#FFF1C7' ? '#172126' : '#ffffff';
      context.font = `${Math.max(8, Math.floor(options.scale * 0.45))}px ui-monospace, monospace`;
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillText(color.symbol, x * options.scale + options.scale / 2, y * options.scale + options.scale / 2);
    }
  });

  if (options.includeGrid) {
    context.strokeStyle = 'rgba(23, 33, 38, 0.18)';
    context.lineWidth = 1;
    for (let x = 0; x <= design.width; x += 1) {
      context.beginPath();
      context.moveTo(x * options.scale + 0.5, 0);
      context.lineTo(x * options.scale + 0.5, canvas.height);
      context.stroke();
    }
    for (let y = 0; y <= design.height; y += 1) {
      context.beginPath();
      context.moveTo(0, y * options.scale + 0.5);
      context.lineTo(canvas.width, y * options.scale + 0.5);
      context.stroke();
    }
  }

  return canvas;
}

export function exportPng(design: BeadDesign, palette: BeadPalette, options: ExportOptions) {
  const canvas = renderDesignToCanvas(design, palette, options);
  canvas.toBlob((blob) => {
    if (blob) downloadBlob(blob, `${design.title.replace(/\s+/g, '-').toLowerCase()}-pattern.png`);
  }, 'image/png');
}

export function exportJson(design: BeadDesign, palette: BeadPalette) {
  const payload = {
    ...design,
    palette
  };
  downloadBlob(
    new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' }),
    `${design.title.replace(/\s+/g, '-').toLowerCase()}-project.json`
  );
}

export function exportCsv(design: BeadDesign, palette: BeadPalette, locale: Locale = 'en') {
  const rows = [csvHeaders(locale)];
  Object.entries(design.counts)
    .sort((a, b) => b[1] - a[1])
    .forEach(([colorId, quantity]) => {
      const color = getPaletteColor(palette, colorId);
      rows.push([color.id, colorName(locale, color), color.brand, color.hex, color.symbol, String(quantity)]);
    });

  const csv = rows.map((row) => row.map((cell) => `"${String(cell).replaceAll('"', '""')}"`).join(',')).join('\n');
  downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `${design.title.replace(/\s+/g, '-').toLowerCase()}-counts.csv`);
}

export function exportPdf(design: BeadDesign, palette: BeadPalette, options: ExportOptions, locale: Locale = 'en') {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const canvas = renderDesignToCanvas(design, palette, {
    ...options,
    includeGrid: true,
    includeSymbols: design.width <= 58,
    scale: design.width <= 58 ? 10 : 7,
    background: 'white'
  });
  const preview = canvas.toDataURL('image/png');
  const imageSize = Math.min(pageWidth - 96, 360);

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(20);
  pdf.text(design.title, 48, 52);

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.text(t(locale, 'pdfMeta', { width: design.width, height: design.height, total: totalBeads(design.counts) }), 48, 72);
  pdf.addImage(preview, 'PNG', 48, 96, imageSize, imageSize);

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.text(t(locale, 'pdfBeadCounts'), 48, imageSize + 128);

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  let y = imageSize + 150;
  Object.entries(design.counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 26)
    .forEach(([colorId, quantity]) => {
      const color = getPaletteColor(palette, colorId);
      pdf.setFillColor(color.hex);
      pdf.rect(48, y - 8, 10, 10, 'F');
      pdf.text(`${color.symbol}  ${colorName(locale, color)} (${color.id})`, 66, y);
      pdf.text(String(quantity), 280, y, { align: 'right' });
      y += 15;
    });

  pdf.setFontSize(8);
  pdf.setTextColor(90);
  pdf.text(t(locale, 'pdfFooter'), 48, 748);
  pdf.save(`${design.title.replace(/\s+/g, '-').toLowerCase()}-pattern.pdf`);
}
